/**
 * MakeAPageTab.tsx
 * Main tab for creating and managing custom pages.
 * Block editor, insert bar, and page editor are in components/.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  loadCustomPages,
  saveCustomPage,
  deleteCustomPage,
  type CustomPage,
} from "@/utils/customPageStore";
import PageEditor from "./components/PageEditor";

export default function MakeAPageTab({ isDark }: { isDark: boolean }) {
  const navigate = useNavigate();
  const [pages, setPages] = useState<CustomPage[]>(() => loadCustomPages());
  const [editing, setEditing] = useState<CustomPage | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const rowHover = isDark ? "hover:bg-gray-700/30" : "hover:bg-gray-50";

  const handleSave = (page: CustomPage) => {
    saveCustomPage(page);
    setPages(loadCustomPages());
    setEditing(null);
  };

  const handleDelete = (id: string) => {
    deleteCustomPage(id);
    setPages(loadCustomPages());
    setDeleteConfirm(null);
  };

  const handleDuplicate = (page: CustomPage) => {
    const now = new Date().toISOString();
    const newSlug = `${page.slug}-copy-${Date.now().toString(36).slice(-4)}`;
    const duplicate: CustomPage = {
      ...page,
      id: `page-${Date.now()}`,
      slug: newSlug,
      title: `${page.title} (Copy)`,
      visible: false,
      blocks: page.blocks.map((b) => ({
        ...b,
        id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      })),
      createdAt: now,
      updatedAt: now,
    };
    saveCustomPage(duplicate);
    setPages(loadCustomPages());
  };

  const handleToggleVisible = (page: CustomPage) => {
    const updated = { ...page, visible: !page.visible, updatedAt: new Date().toISOString() };
    saveCustomPage(updated);
    setPages(loadCustomPages());
  };

  const startNew = () => {
    setEditing({
      id: "new",
      slug: "",
      title: "",
      visible: false,
      blocks: [],
      createdAt: "",
      updatedAt: "",
    });
  };

  if (editing) {
    return (
      <PageEditor
        page={editing}
        onSave={handleSave}
        onCancel={() => setEditing(null)}
        isDark={isDark}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            Make a Page
          </h2>
          <p className={`text-sm ${subColor}`}>
            Create custom pages for your site — About Us, FAQ, Policies, Events, and more. Each page gets its own URL.
          </p>
        </div>
        <button
          onClick={startNew}
          className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 text-white font-bold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line"></i>
          New Page
        </button>
      </div>

      <div className={`rounded-2xl border p-5 ${cardBg}`}>
        <h3 className={`font-bold text-sm mb-3 ${headingColor}`}>
          <i className="ri-information-line text-pink-400 mr-1.5"></i>
          How It Works
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { step: "1", title: "Create a Page", desc: "Give it a title and URL slug (e.g. /about-us)" },
            { step: "2", title: "Add Content Blocks", desc: "Hover between blocks to insert anywhere — headings, text, images, CTAs, two-column layouts" },
            { step: "3", title: "Publish It", desc: "Toggle visibility to make it live. Share the URL with anyone." },
          ].map((item) => (
            <div key={item.step} className={`rounded-xl p-4 ${isDark ? "bg-gray-700/50" : "bg-gray-50"}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-pink-500 text-white text-xs font-bold flex-shrink-0">
                  {item.step}
                </div>
                <span className={`text-xs font-bold ${headingColor}`}>{item.title}</span>
              </div>
              <p className={`text-xs leading-relaxed ${subColor}`}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            <i className="ri-pages-line text-pink-400 mr-1.5"></i>
            Your Custom Pages ({pages.length})
          </h3>
        </div>

        {pages.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-pink-50 mx-auto mb-4">
              <i className="ri-file-add-line text-pink-400 text-2xl"></i>
            </div>
            <p className={`text-base font-bold mb-1 ${headingColor}`}>No custom pages yet</p>
            <p className={`text-sm mb-5 ${subColor}`}>
              Create your first page — an About Us, a Policies page, an Events page, anything you need.
            </p>
            <button
              onClick={startNew}
              className="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-add-line mr-1"></i>Create First Page
            </button>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: isDark ? "#374151" : "#f3f4f6" }}>
            {pages.map((page) => (
              <div key={page.id} className={`flex items-center gap-4 px-5 py-4 transition-colors ${rowHover}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
                  <i className="ri-file-text-line text-pink-400 text-base"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm ${headingColor}`}>{page.title || "Untitled Page"}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs font-mono ${subColor}`}>/{page.slug}</span>
                    <span className={`text-xs ${subColor}`}>·</span>
                    <span className={`text-xs ${subColor}`}>{page.blocks.length} blocks</span>
                    <span className={`text-xs ${subColor}`}>·</span>
                    <span className={`text-xs ${subColor}`}>
                      Updated {new Date(page.updatedAt || page.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleVisible(page)}
                    className={`text-xs font-bold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                      page.visible
                        ? isDark ? "bg-green-900/40 text-green-400 hover:bg-green-900/60" : "bg-green-50 text-green-600 hover:bg-green-100"
                        : isDark ? "bg-gray-700 text-gray-500 hover:bg-gray-600" : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                    }`}
                  >
                    {page.visible ? "Live" : "Hidden"}
                  </button>
                  <button
                    onClick={() => navigate(`/${page.slug}`)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                    title="Visit page"
                  >
                    <i className="ri-external-link-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => setEditing(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                    title="Edit page"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => handleDuplicate(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
                    title="Duplicate page"
                  >
                    <i className="ri-file-copy-line text-sm"></i>
                  </button>
                  {deleteConfirm === page.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(page.id)} className="text-xs font-bold text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap">Delete</button>
                      <button onClick={() => setDeleteConfirm(null)} className={`text-xs cursor-pointer whitespace-nowrap ${subColor}`}>Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(page.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors text-red-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete page"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {pages.length > 0 && (
        <div className={`rounded-2xl border p-4 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-amber-50 border-amber-200"}`}>
          <p className={`text-xs ${isDark ? "text-gray-400" : "text-amber-700"}`}>
            <i className="ri-information-line mr-1"></i>
            <strong>Tip:</strong> Custom pages are accessible at <span className="font-mono">/:slug</span> (e.g. /about-us, /events, /policies). Share the URL directly with visitors. To add a link in the navbar or footer, ask your developer to add it to the navigation components.
          </p>
        </div>
      )}
    </div>
  );
}